/**
 * https://ui.shadcn.com/docs/components/calendar
 */
"use client";
import { useEffect, useState } from "react";
import type { DateRange } from "react-day-picker";
import { Temporal } from "temporal-polyfill";
import { Calendar } from "./calendar";
import { Icon } from "./icon";
import { Separator } from "./separator";
import { Typography } from "./typography";

interface DateRangePickerProps {
  maxDate?: Temporal.PlainDate;
  selectedDateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
  onDateRangeSelected: (value: [Temporal.PlainDate, Temporal.PlainDate]) => void;
}

export function DateRangePicker({ maxDate, selectedDateRange, onDateRangeSelected }: DateRangePickerProps) {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  // Convert Temporal dates to JavaScript Date objects for the Calendar component
  useEffect(() => {
    if (!selectedDateRange) {
      setDateRange(undefined);
      return;
    }

    setDateRange({
      from: new Date(selectedDateRange[0].year, selectedDateRange[0].month - 1, selectedDateRange[0].day),
      to: new Date(selectedDateRange[1].year, selectedDateRange[1].month - 1, selectedDateRange[1].day),
    });
  }, [selectedDateRange]);

  const handleSelect = (range: DateRange | undefined) => {
    setDateRange(range);

    if (range?.from && range?.to) {
      // Convert back to Temporal.PlainDate for the callback
      onDateRangeSelected([
        Temporal.PlainDate.from({
          year: range.from.getFullYear(),
          month: range.from.getMonth() + 1,
          day: range.from.getDate(),
        }),
        Temporal.PlainDate.from({
          year: range.to.getFullYear(),
          month: range.to.getMonth() + 1,
          day: range.to.getDate(),
        }),
      ]);
    }
  };

  const disabledDays = maxDate
    ? {
        after: new Date(maxDate.year, maxDate.month - 1, maxDate.day),
      }
    : undefined;

  return (
    <div>
      <div>
        <div className="flex w-full pb-4 px-4">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <Typography key={day} size="xs" color="textMuted" className="flex-1 text-center">
              {day}
            </Typography>
          ))}
        </div>
        <Separator />
      </div>
      <Calendar
        initialFocus
        mode="range"
        defaultMonth={dateRange?.from}
        selected={dateRange}
        onSelect={handleSelect}
        numberOfMonths={1}
        disabled={disabledDays}
        className="w-full p-4"
        classNames={{
          months: "w-full",
          month: "w-full",
          caption: "flex items-center justify-center gap-1 mb-4 relative",
          caption_label: "text-sm font-semibold text-textPrimary",
          nav: "flex absolute left-2 right-2 justify-between",
          nav_button: "h-7 w-7 bg-transparent p-0 hover:opacity-50",
          nav_button_previous: "",
          nav_button_next: "",
          head: "hidden",
          row: "flex w-full mt-2",
          cell: "flex-1 flex items-center justify-center relative p-0",
          day: "h-9 w-9 p-0 font-semibold hover:bg-gray-100 relative flex items-center justify-center",
          day_selected: "",
          day_range_start: "bg-black text-white hover:bg-black hover:text-white rounded-full bg-backgroundDark",
          day_range_middle: "w-full bg-backgroundDark hover:bg-backgroundDark rounded-none",
          day_range_end: "bg-black text-white hover:bg-black hover:text-white rounded-full",
          day_today: "border border-black rounded-full",
          day_outside: "text-gray-300 opacity-50",
          day_disabled: "opacity-50",
          day_hidden: "invisible",
        }}
        components={{
          IconLeft: () => <Icon name="arrow-left" iconSize="16px" />,
          IconRight: () => <Icon name="arrow-right" iconSize="16px" />,
        }}
      />
    </div>
  );
}
